import { CaretDown, Check, Gear, SignOut } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useActionContext } from "~/actions/context";
import { signOutAction } from "~/actions/definitions/settings";
import { useCurrentWorkspace, useSwitchWorkspace, useWorkspaces } from "~/api/queries";
import { ABILITY } from "~/api/types";
import { Menu, type MenuItem } from "~/foundation/components/Menu";

export function WorkspaceMenu() {
  const { t } = useTranslation();
  const { data: current } = useCurrentWorkspace();
  const { data: workspaces } = useWorkspaces();
  const switchWorkspace = useSwitchWorkspace();
  const context = useActionContext({ source: "contextmenu" });

  const items: MenuItem[] = [
    { type: "heading", title: t("Workspaces") },
    ...(workspaces ?? []).map<MenuItem>((workspace) => {
      const isCurrent = workspace.current || workspace.id === current?.id;
      return {
        type: "button",
        title: workspace.name,
        ...(isCurrent ? { icon: Check, selected: true } : {}),
        // A suspended workspace cannot be entered — the server would refuse anyway, so do
        // not offer it as if it were available.
        ...(workspace.suspended ? { disabled: true } : {}),
        onClick: () => {
          if (isCurrent || workspace.suspended || switchWorkspace.isPending) {
            return;
          }
          switchWorkspace.mutate({ workspaceId: workspace.id });
        },
      };
    }),
    { type: "separator" },
    ...(current?.abilities[ABILITY.workspaceUpdate]
      ? [{ type: "route" as const, title: t("Workspace settings"), icon: Gear, to: "/settings" }]
      : []),
    {
      type: "button",
      title: t("Sign out"),
      icon: SignOut,
      onClick: () => {
        void signOutAction.perform?.(context);
      },
    },
  ];

  return (
    <Menu
      ariaLabel={t("Workspace menu")}
      items={items}
      trigger={
        <button
          type="button"
          className="flex h-8 max-w-[180px] items-center gap-1 rounded-sm px-2 text-body font-medium text-text hover:bg-hover-bg"
        >
          <span className="truncate">{current?.name ?? t("Workspace")}</span>
          <CaretDown size={12} aria-hidden className="shrink-0 text-text-tertiary" />
        </button>
      }
    />
  );
}
